import { ArrowLeft } from "lucide-react"

import TopGlow from "@/assets/img/soulmate/top-glow.png"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { StepComponentProps } from "./step-contract"

const CHIP_TONES = [
  { text: "text-peach", fill: "var(--peach)" },
  { text: "text-gold", fill: "var(--gold)" },
  { text: "text-cyan", fill: "var(--cyan)" },
] as const

export interface AuraArchetypeMeta {
  kicker: string
  chips: [string, string, string]
  balanceHeading: string
  balanceBody: string
}

const ARCHETYPES: Record<string, AuraArchetypeMeta> = {
  "archetype-radiant": {
    kicker: "Your Aura Archetype",
    chips: ["Warmth", "Optimism", "Support"],
    balanceHeading: "When out of balance",
    balanceBody: "You may give away more energy than you restore",
  },
  "archetype-grounded": {
    kicker: "Your Aura Archetype",
    chips: ["Calm", "Reliability", "Patience"],
    balanceHeading: "When out of balance",
    balanceBody: "You may hold too much inside or keep distance",
  },
  "archetype-empath": {
    kicker: "Your Aura Archetype",
    chips: ["Intuition", "Compassion", "Awareness"],
    balanceHeading: "When out of balance",
    balanceBody: "You may absorb others’ moods and get overwhelmed",
  },
}

/**
 * Aura archetype interstitial (Step8.1–8.3): kicker, uppercase title, hero,
 * tone chips, and an “out of balance” note. Copy extras are keyed by step id.
 */
export function AuraArchetypeStep({ step, onAdvance, onBack }: StepComponentProps) {
  const meta = ARCHETYPES[step.id]

  return (
    <div
      className="relative flex h-dvh min-h-dvh flex-col overflow-hidden bg-dark-base text-purple-light"
      data-slot="aura-archetype-step"
    >
      <img
        src={TopGlow}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px] w-full object-cover"
      />

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div className="flex h-14 shrink-0 items-center px-4">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            className="-ml-2 flex size-10 items-center justify-center rounded-full text-purple-light transition-colors hover:bg-purple-light/10"
          >
            <ArrowLeft className="size-6" />
          </button>
        </div>

        <div className="shrink-0 space-y-2 px-4 text-center">
          {meta && <p className="text-base capitalize text-main-purple">{meta.kicker}</p>}
          <h1 className="font-display text-2xl font-medium uppercase text-purple-light">
            {step.title}
          </h1>
          {step.subtitle && <p className="text-base text-purple-light/70">{step.subtitle}</p>}
        </div>

        {step.coverImage && (
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <img
              src={step.coverImage}
              alt=""
              className="h-[304px] max-h-full w-auto max-w-none object-contain mix-blend-lighten scale-[1.1]"
         
            />
          </div>
        )}

        <div className="flex shrink-0 flex-col gap-4 px-4 pb-4">
          {meta && (
            <div className="flex flex-col items-center gap-3">
              <div className="flex w-full gap-2">
                {meta.chips.map((label, index) => (
                  <span
                    key={label}
                    className={cn(
                      "flex flex-1 items-center justify-center rounded-[40px] px-4 py-2 text-center text-xs font-semibold uppercase backdrop-blur-[6px]",
                      CHIP_TONES[index].text,
                    )}
                    style={{
                      backgroundImage: `linear-gradient(0deg, color-mix(in srgb, ${CHIP_TONES[index].fill} 20%, transparent), color-mix(in srgb, ${CHIP_TONES[index].fill} 20%, transparent))`,
                      backgroundColor: "color-mix(in srgb, var(--dark-base) 40%, transparent)",
                    }}
                  >
                    {label}
                  </span>
                ))}
              </div>
              <div className="h-px w-full bg-purple-light/10" />
              <div className="space-y-1 text-center">
                <p className="text-base font-medium capitalize text-purple-light">
                  {meta.balanceHeading}
                </p>
                <p className="text-base text-purple-light/70">{meta.balanceBody}</p>
              </div>
            </div>
          )}

          <Button variant="gradient" size="xl" onClick={onAdvance}>
            {step.cta ?? "Continue"}
          </Button>
        </div>
      </div>
    </div>
  )
}
